import { useCallback, useEffect, useRef, useState } from 'react'
import useApi from '../hooks/useApi'
import useAccessScope from '../hooks/useAccessScope'
import useInterruptibleMutation from '../hooks/useInterruptibleMutation'
import { formatControlRequestStatus, getApiErrorMessage, getCSRFToken } from '../lib/utils'
import { DelegatedAccessLevel, formatDelegatedAccessLevel } from '../lib/delegatedAccess'
import { Button, InlineAlert, LoadingSpinner } from './BaseComponents'
import CanonicalModal from './BaseComponents/CanonicalModal/CanonicalModal'
import SimpleSortTable, { TableColumn } from './BaseComponents/SimpleSortTable/SimpleSortTable'
import DelegatedAccessLevelModal from './DelegatedAccessLevelModal'

type Grant = {
	_id: string
	id: string
	status: string
	accessLevel: DelegatedAccessLevel
	delegateUserName?: string
	delegateCompanyName?: string
}

type Action = { kind: 'approve' | 'changeLevel' | 'decline' | 'revoke'; grant: Grant }

function AdvisorsTable() {
	const [loading, setLoading] = useState(false)
	const [data, setData] = useState<Grant[]>([])
	const [action, setAction] = useState<Action | null>(null)
	const [saving, setSaving] = useState(false)
	// #882 (item 5): a rejecting write (e.g. the now-rejecting READ_ONLY_ACCESS/
	// ACCESS_AUDIT_UNAVAILABLE notify-only codes - #857 item 6) previously had no catch here at
	// all, so it surfaced as an unhandled rejection with no visible feedback.
	const [actionError, setActionError] = useState('')
	// Issue #884: this GET (unlike the writes above) runs on mount and after every successful
	// write, and this page is reachable while assuming another organisation, so it can receive
	// the now-rejecting ACCESS_AUDIT_UNAVAILABLE (#857 item 6) too. Previously had no catch at
	// all, leaving `loading` stuck true (the spinner below never clears) plus an unhandled
	// rejection.
	const [loadError, setLoadError] = useState('')
	const api = useApi()
	const { generation, isCurrent } = useAccessScope(api)
	const [loadedGeneration, setLoadedGeneration] = useState<number>()
	const loadController = useRef<AbortController>()
	const startMutation = useInterruptibleMutation(api, undefined,
		'The advisor access change was interrupted and may already have been saved. Reopen the advisor list and check the access before trying again.')

	const loadData = useCallback(async () => {
		loadController.current?.abort()
		const controller = new AbortController()
		loadController.current = controller
		setLoading(true)
		setLoadError('')
		try {
			const result = await api.get('/delegatedAccess/forMyOrganisation?limit=100', { signal: controller.signal })
			if (!controller.signal.aborted && isCurrent(generation)) {
				setData(result.data.map((grant: Omit<Grant, '_id'>) => ({ ...grant, _id: grant.id })))
			}
		} catch (err) {
			if (!controller.signal.aborted && isCurrent(generation)) {
				setLoadError(getApiErrorMessage(err, 'Failed to load advisor access requests. Please try again.'))
			}
		} finally {
			if (!controller.signal.aborted && isCurrent(generation)) setLoading(false)
		}
	}, [api, generation, isCurrent])

	useEffect(() => {
		setData([])
		setAction(null)
		setSaving(false)
		setActionError('')
		setLoadedGeneration(generation)
		loadData()
		return () => loadController.current?.abort()
	}, [generation, loadData])

	const run = async (send: (csrf: string, signal: AbortSignal) => Promise<unknown>) => {
		if (loading || saving || loadedGeneration !== generation) return
		const { signal, onSubmitted, finish } = startMutation()
		setSaving(true)
		setActionError('')
		try {
			const csrf = await getCSRFToken(signal)
			if (signal.aborted || !isCurrent(generation)) return
			onSubmitted()
			await send(csrf, signal)
			if (signal.aborted || !isCurrent(generation)) return
		} catch (err) {
			if (!signal.aborted && isCurrent(generation)) {
				setActionError(getApiErrorMessage(err, 'Failed to update this advisor access. Please try again.'))
			}
			return
		} finally {
			finish()
			if (!signal.aborted && isCurrent(generation)) {
				setSaving(false)
				setAction(null)
			}
		}
		loadData()
	}

	const approve = (grant: Grant, level: DelegatedAccessLevel) =>
		run((csrf, signal) =>
			api.post(`delegatedAccess/${grant.id}/decide`, { decision: 'approve', level, _csrf: csrf }, { signal })
		)

	const changeLevel = (grant: Grant, level: DelegatedAccessLevel) =>
		run((csrf, signal) => api.put(`delegatedAccess/${grant.id}/level`, { level, _csrf: csrf }, { signal }))

	const decline = (grant: Grant) =>
		run((csrf, signal) =>
			api.post(`delegatedAccess/${grant.id}/decide`, { decision: 'reject', _csrf: csrf }, { signal })
		)

	const revoke = (grant: Grant) =>
		run((csrf, signal) => api.delete(`delegatedAccess/${grant.id}`, { _csrf: csrf }, { signal }))

	if (loading || loadedGeneration !== generation) {
		return <LoadingSpinner />
	}

	// Issue #884: a failed refresh (e.g. after a successful write - see run() above) must not
	// blank out data that is still genuinely on screen, nor claim "no advisors" when the real
	// state is simply unknown because the load failed. Only the truly-empty, no-error case gets
	// the friendly empty message below; a load failure instead falls through to the table (which
	// renders its existing/empty data) with the loadError banner shown above it, same placement
	// as actionError.
	if (!data.length && !loadError) {
		return <div className='text-white px-4 py-2 mt-2'>No advisors have requested access to your organisation</div>
	}

	const columns: TableColumn[] = [
		{ id: 'delegateUserName', label: 'Advisor' },
		{ id: 'delegateCompanyName', label: 'Organisation name' },
		{
			id: 'status',
			label: 'Status',
			renderer: (key, row) => <span>{formatControlRequestStatus(row[key])}</span>,
		},
		{
			id: 'accessLevel',
			label: 'Access level',
			renderer: (key, row) => <span>{row.status === 'active' ? formatDelegatedAccessLevel(row[key]) : ''}</span>,
		},
		{
			id: 'actions',
			label: 'Actions',
			textAlign: 'center',
			renderer: (key, row) => {
				const grant = row as Grant
				return (
					<div className='flex space-x-2 justify-center'>
						{grant.status === 'pending' && (
							<>
								<Button
									type='Primary'
									size='sm'
									isTableRowButton
									text='Approve'
									onClick={() => setAction({ kind: 'approve', grant })}
								/>
								<Button
									type='Primary'
									size='sm'
									isTableRowButton
									text='Decline'
									onClick={() => setAction({ kind: 'decline', grant })}
								/>
							</>
						)}
						{grant.status === 'active' && (
							<>
								<Button
									type='Primary'
									size='sm'
									isTableRowButton
									text='Change access'
									onClick={() => setAction({ kind: 'changeLevel', grant })}
								/>
								<Button
									type='Primary'
									size='sm'
									isTableRowButton
									text='Revoke access'
									onClick={() => setAction({ kind: 'revoke', grant })}
								/>
							</>
						)}
					</div>
				)
			},
		},
	]

	const advisorName = action?.grant.delegateUserName ?? ''
	const levelModalKind = action?.kind === 'approve' || action?.kind === 'changeLevel' ? action.kind : null
	const confirmKind = action?.kind === 'decline' || action?.kind === 'revoke' ? action.kind : null

	return (
		<>
			{actionError && <InlineAlert type='error' className='mb-4'>{actionError}</InlineAlert>}
			{loadError && <InlineAlert type='error' className='mb-4'>{loadError}</InlineAlert>}
			<SimpleSortTable data={data} columns={columns} />
			<DelegatedAccessLevelModal
				open={!!levelModalKind}
				title={levelModalKind === 'approve' ? 'Approve access request' : 'Change access level'}
				subtitle={advisorName}
				confirmText={levelModalKind === 'approve' ? 'Approve' : 'Save'}
				initialLevel={action?.grant.accessLevel ?? 'view'}
				loading={saving}
				onClose={() => setAction(null)}
				onConfirm={(level) =>
					action && (levelModalKind === 'approve' ? approve(action.grant, level) : changeLevel(action.grant, level))
				}
			/>
			<CanonicalModal
				open={!!confirmKind}
				onClose={() => setAction(null)}
				title={confirmKind === 'revoke' ? 'Revoke access' : 'Decline access request'}
				subtitle={advisorName}
				actions={
					<>
						<Button
							type='Primary'
							text={confirmKind === 'revoke' ? 'Revoke access' : 'Decline'}
							disabled={saving}
							onClick={() => action && (confirmKind === 'revoke' ? revoke(action.grant) : decline(action.grant))}
							additionalClass='mr-5'
						/>
						<Button type='Outlined' text='Cancel' onClick={() => setAction(null)} />
					</>
				}
			>
				<p className='text-white mt-8'>
					{confirmKind === 'revoke'
						? 'This advisor will immediately lose access to your organisation.'
						: 'This advisor will not get access to your organisation.'}
				</p>
			</CanonicalModal>
		</>
	)
}

export default AdvisorsTable
